import {forwardRef, useMemo} from "react";
import {IProtocol, TProtocolStatus} from "../../protocols/types/protocol";
import {IProtocolDefinition} from "../../protocols/types/protocolDefinitions";
import {Box, BoxProps, Combobox, InputBase, Text, useCombobox} from "@mantine/core";
import {IconChevronDown} from "@tabler/icons-react";
import commonClasses from "../common.module.css";
import cx from "clsx";

interface ProtocolSelectorProps extends BoxProps {
    /**
     * Protocols that are currently in progress
     */
    protocols?: Array<IProtocol>
    /**
     * Protocols to allow selection from
     */
    protocolDefinitions?: Array<IProtocolDefinition>
    /**
     * The selected protocol
     */
    selectedProtocol?: IProtocolDefinition | IProtocol
    /**
     * Whether this control should be disabled or not
     */
    disabled?: boolean
    onProtocolSelection: (protocol: IProtocolDefinition | IProtocol) => void
    dataTestId?: string
}

export const ProtocolSelector = forwardRef<HTMLButtonElement, ProtocolSelectorProps>(({
                                                                                          protocols,
                                                                                          selectedProtocol,
                                                                                          protocolDefinitions,
                                                                                          onProtocolSelection,
                                                                                          disabled = false,
                                                                                          ...others
                                                                                      }, ref) => {
        const combobox = useCombobox({
            onDropdownClose: () => combobox.resetSelectedOption(),
        });
        const inProgressProtocols = useMemo(() => protocols?.filter((p) => p.status === TProtocolStatus.IN_PROGRESS), [protocols])
        const renderProtocolOptions = (ps?: Array<IProtocolDefinition | IProtocol>) => {
            return ps?.map((p) => <Combobox.Option key={p.id} value={p.id}>{p.name}</Combobox.Option>) || <></>
        }
        return (
            <Box {...others}>
                <Combobox
                    store={combobox}
                    onOptionSubmit={(value) => {
                        combobox.closeDropdown()
                        const selected = protocols?.find((p) => p.id === value) ||
                            protocolDefinitions?.find((p) => p.id === value)
                        selected && onProtocolSelection(selected)
                    }}
                >
                    <Combobox.Target>
                        <InputBase
                            ref={ref}
                            classNames={{input: cx(!disabled && commonClasses.brightnessHover)}}
                            disabled={disabled}
                            label="Select Protocol"
                            component="button"
                            type="button"
                            pointer
                            rightSection={<IconChevronDown size="1rem"/>}
                            rightSectionPointerEvents="none"
                            onClick={() => combobox.toggleDropdown()}
                        >
                            <Text style = {{textOverflow:"ellipsis", textWrap:"nowrap", overflow:"hidden"}}>{selectedProtocol?.name}</Text>
                        </InputBase>
                    </Combobox.Target>
                    <Combobox.Dropdown mah={200} style={{overflowY:"auto"}}>
                        <Combobox.Options>
                            <Combobox.Group label="In Progress" aria-label="In progress protocols">
                                {renderProtocolOptions(inProgressProtocols)}
                            </Combobox.Group>
                            <Combobox.Group label={"Protocols"}>
                                {renderProtocolOptions(protocolDefinitions)}
                            </Combobox.Group>
                        </Combobox.Options>
                    </Combobox.Dropdown>

                </Combobox>
            </Box>
        )
    }
)